"use client";

import { useTranslations } from "next-intl";
import {
  Footer as DesignSystemFooter,
  type TFooterProps as TDesignSystemFooterProps,
} from "@/ui/components/navigation/Footer";
import { cn } from "@/ui/lib";
import {
  getFooterPrimaryNavigation,
  getFooterSecondaryNavigation,
  getFooterSignedInPrimaryNavigation,
  getFooterSocialNavigation,
} from "apps/website/utils/footer/footer-navigation";
import {
  applySignUpFunnelLinks,
} from "apps/website/utils/footer/sign-up-funnel-links";

import { CountryToggle } from "./CountryToggle";
import { useFooterNewsletterSignup } from "./useFooterNewsletterSignup";

type TNavigationProps = "primaryNavigation" | "secondaryNavigation" | "socialNavigation";
type TManagedProps = "newsletter" | "countryToggle";

export type TFooterProps = Omit<TDesignSystemFooterProps, TNavigationProps | TManagedProps>
  & Partial<Pick<TDesignSystemFooterProps, TNavigationProps>>
  & {
    /** Replaces the plain sign-up link on funnel pages, see getSignUpFunnelHref */
    signUpHref?: string | null;
  };

export default function Footer({
  isSignedIn = false,
  signUpHref,
  primaryNavigation,
  secondaryNavigation,
  socialNavigation,
  className,
  ...props
}: TFooterProps) {
  const t = useTranslations("Components.Footer.Navigation");
  const newsletter = useFooterNewsletterSignup();
  const basePrimaryNavigation = primaryNavigation
    ?? (isSignedIn ? getFooterSignedInPrimaryNavigation(t) : getFooterPrimaryNavigation(t));
  const resolvedPrimaryNavigation = applySignUpFunnelLinks(basePrimaryNavigation, signUpHref);

  return (
    <DesignSystemFooter
      { ...props }
      primaryNavigation={ resolvedPrimaryNavigation }
      secondaryNavigation={ secondaryNavigation ?? getFooterSecondaryNavigation(t) }
      socialNavigation={ socialNavigation ?? getFooterSocialNavigation(t) }
      newsletter={ newsletter }
      countryToggle={ <CountryToggle /> }
      className={ cn("pb-24", className) }
    />
  );
}
